/**
 * The group and ledger editors used by the chart of accounts.
 * @file src/web/features/admin/components/masters/ChartEditors.tsx
 *
 * Lifted out of masters.tsx when Groups and Ledgers became one tree, so the
 * page file holds tabs and the tree holds its own editors. GroupEditor is the
 * old GroupModal unchanged; LedgerEditor wraps the existing LedgerModal, which
 * already handles both create and edit.
 */

import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetch } from '../../lib/admin-api';
import { LedgerModal, type LedgerLite } from '../LedgerModal';

interface Group { id: string; name: string; parent: string | null; nature: string | null }
const NATURES = ['asset', 'liability', 'income', 'expense'] as const;

/** The same one-line wrapper masters.tsx uses; copied rather than exported so
 *  the page file and this one can diverge without one dragging the other. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-ink-600">{label}</label>
      {children}
    </div>
  );
}

export function GroupEditor({ group, groups, entityId, onClose, onSaved }: { group: Group | null; groups: Group[]; entityId: string | null; onClose: () => void; onSaved: () => void }) {
  const editing = !!group;
  const [name, setName] = useState(group?.name ?? '');
  const [parent, setParent] = useState(group?.parent ?? '');
  const [nature, setNature] = useState(group?.nature ?? '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) { toast.error('Group name is required'); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ name: name.trim(), parent: parent.trim(), nature: nature || null });
      const q = { entityId: entityId ?? undefined };
      const res = editing
        ? await adminFetch(`/api/books/groups/${group!.id}`, { method: 'PATCH', query: q, body })
        : await adminFetch('/api/books/groups', { method: 'POST', query: q, body });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.message || 'Failed');
      toast.success(editing ? 'Group saved' : 'Group created'); onSaved();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not save'); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-6 shadow-xl">
        <button onClick={onClose} className="absolute right-4 top-4 text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        <h3 className="text-base font-semibold text-ink-900">{editing ? 'Edit group' : 'New group'}</h3>
        <div className="mt-4 space-y-3">
          <Field label="Group name"><input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl border border-ink-200 bg-paper px-3 py-2 text-sm focus:border-ink-300 focus-visible:outline-none" /></Field>
          <Field label="Under (parent group)">
            <select value={parent} onChange={(e) => setParent(e.target.value)} className="w-full rounded-xl border border-ink-200 bg-paper px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-highlighter">
              <option value="">— Primary group (top level)</option>
              {groups.filter((g) => g.id !== group?.id).map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select>
          </Field>
          <Field label="Nature">
            <select value={nature} onChange={(e) => setNature(e.target.value)} className="w-full rounded-xl border border-ink-200 bg-paper px-3 py-2 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-highlighter">
              <option value="">Unclassified</option>
              {NATURES.map((n) => <option key={n} value={n} className="capitalize">{n}</option>)}
            </select>
          </Field>
          <p className="text-[11px] text-ink-400">Nature decides whether this group's ledgers land on the Balance Sheet or the P&amp;L. Renaming updates every ledger and sub-group under it.</p>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:opacity-50">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? 'Save group' : 'Create group'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Ledgers ────────────────────────────────────────────────────────────── */

export function LedgerEditor({ ledger, entityId, onClose, onSaved }: {
  ledger: LedgerLite | null; entityId: string | null; onClose: () => void; onSaved: () => void;
}) {
  return <LedgerModal entityId={entityId} ledger={ledger} onClose={onClose} onSaved={onSaved} />;
}
